import { formatCurrency, formatDate, formatNumber } from '@angular/common';
import { Injectable, LOCALE_ID, inject } from '@angular/core';

import { Page } from '../client-config/page.client-config';
import { UserService } from '../user/user.service';

/**
 * @stable
 */
export class MonthInfo {
    numeric: number;
    shortName: string;
    longName: string;
}

/**
 * @whatItDoes A helper service that provides utility methods for internationalization.
 *
 * @description
 *
 * ## formatCurrency
 *
 * See [vnCurrency](http://docs.vanilla.intranet/mobile/api/core/CurrencyPipe) for details. Also, special
 * `bwin` format is available to format as <amount><space><currencyCode>.
 *
 * ## getMonths
 *
 * Gets a list of months with their long and short names.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class IntlService {
    private readonly user = inject(UserService);
    private readonly page = inject(Page);
    private readonly localeId = inject(LOCALE_ID);

    formatCurrency(amount: number, currencyCode?: string, digitsInfo?: string): string {
        currencyCode = currencyCode || this.user.currency;
        const display = this.page.currency[currencyCode.toLowerCase()] ?? this.page.currency['default'];
        if (display === 'bwin') {
            digitsInfo = digitsInfo || '1.2-2';
            return `${this.formatNumber(amount, digitsInfo)} ${currencyCode}`;
        } else {
            let currency: string = currencyCode || 'USD';
            if (display !== 'code') {
                currency = this.findSymbolOfCurrency(currency, display !== 'symbol');
            }
            return formatCurrency(amount, this.localeId, currency, display, digitsInfo);
        }
    }

    formatCurrencySymbol(currencyCode?: string): string {
        currencyCode = currencyCode || this.user.currency;
        const display = this.page.currency[currencyCode.toLowerCase()] ?? this.page.currency['default'];
        if (display === 'bwin') {
            return currencyCode;
        } else {
            let currency: string = currencyCode || 'USD';
            if (display !== 'code') {
                currency = this.findSymbolOfCurrency(currency, display !== 'symbol');
            }
            return this.findSymbolOfCurrency(currency);
        }
    }

    private findSymbolOfCurrency(userCurrencyCode: string, useNarrow: boolean = false) {
        try {
            let currencyCode: string = userCurrencyCode || 'USD';
            const formatter = new Intl.NumberFormat(this.localeId, {
                style: 'currency',
                currency: currencyCode.toUpperCase(),
                currencyDisplay: useNarrow ? 'narrowSymbol' : 'symbol',
            });

            const parts = formatter.formatToParts(1);
            const symbol = parts.find((part) => part.type === 'currency')?.value;

            return symbol ?? currencyCode;
        } catch {
            return userCurrencyCode;
        }
    }

    formatNumber(amount: number, digitsInfo?: string): string {
        return formatNumber(amount, this.localeId, digitsInfo);
    }

    /**
     If no format is specified, `short` will be used. [Format Options](https://angular.io/api/common/DatePipe#pre-defined-format-options)
     */
    formatDate(date: Date | string | number, format: string = 'short', timezone?: string): string {
        return formatDate(date, format, this.localeId, timezone);
    }

    /**
     If no currency code is specified, user currency code will be used.
     */
    getCurrencySymbol(currencyCode?: string): string {
        return this.findSymbolOfCurrency(currencyCode ?? this.user.currency);
    }

    getMonths(): MonthInfo[] {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((index) => {
            const date = new Date(`${index}/11/2009`);
            return {
                numeric: index,
                longName: date.toLocaleString(this.page.culture, { month: 'long' }),
                shortName: date.toLocaleString(this.page.culture, { month: 'short' }),
            };
        });
    }
}
